import { consoleMsg, waitForContent } from "../shared/utility.js";
import { initializePage } from "../shared/page-init.js";
import { validatePage } from "../shared/validation.js";
import { previous, buildSubmissionData, navsSubmit, displaySubmissionMessage, showToast } from "../shared/navigation.js";
import { generateInspectionPDF } from "../shared/pdf-main.js";
import { combinedVehicleDetails, updateCombinedVehicleDetails } from "../shared/app-data.js";
import SignaturePad from "signature_pad";
import { resizeCanvas } from "../shared/signature-functions.js";
import { renderNavButtons } from "../shared/templates.js";

const app = window.app;

export class Signature extends HTMLElement {
	async connectedCallback() {
		await waitForContent("signature");
		this.innerHTML = app.content.signature;

		this.querySelector("#nav-slot").replaceWith(renderNavButtons("backButton10", "nextButton10", "Submit"));

		this.addEventListener("ionViewWillEnter", async () => {
			/*const pageElement = this;
			await initializePage(10, pageElement);*/
			// Check 'inspection-submitted' flag to see if page has already been submitted successfully
			if (localStorage.getItem("inspection-submitted")) {
				const router = document.querySelector("ion-router");
				await router.push(app.routes.submissionComplete, "root");
				return;
			}
			// If not, initialize Signature page
			const pageElement = this;
			await initializePage(10, pageElement);

			// Disable the refresher when entering the Signature page
			const refresher = this.querySelector("ion-refresher");
			if (refresher) {
				refresher.disabled = true;
				refresher.style.display = "none";
			}
			//await setPrintedName(inspector);
		});
		this.addEventListener("ionViewDidEnter", async () => {
			const wrapper = document.getElementById("signature-pad");
			const clearButton = wrapper.querySelector("[data-action=clear]");
			const savePNGButton = wrapper.querySelector("[data-action=save-png]");
			const canvas = wrapper.querySelector("canvas");
			let capturedSignature = null;
			const signaturePad = new SignaturePad(canvas, {
				// It's Necessary to use an opaque color when saving image as JPEG;
				// this option can be omitted if only saving as PNG or SVG
				backgroundColor: "rgb(255, 255, 255)",
			});
			signaturePad.addEventListener("endStroke", () => {
				capturedSignature = signaturePad.toDataURL();
			});
			const signatureAlert = document.getElementById("signature-alert");
			const missingDataAlert = document.getElementById("missing-data-alert");
			signatureAlert.buttons = [
				{
					text: "OK",

					handler: () => {
						signatureAlert.dismiss();
					},
				},
			];
			clearButton.addEventListener("click", function (event) {
				signaturePad.clear();
				capturedSignature = null;
			});
			missingDataAlert.message = ` <p>You have not completed entering all required data. Please ensure you have 
			answered all required questions on each page of the inspection form.  You are missing data for the following pages:
         </p>
         `;
			missingDataAlert.buttons = [
				{
					text: "OK",
					handler: () => {
						missingDataAlert.dismiss();
					},
				},
			];

			updateCombinedVehicleDetails();

			// Populate ending mileage field only if no user value stored (preserves user edits on back navigation)
			const storedEndingMileage = localStorage.getItem("ending-mileage");
			// NAVS passes this as app.data.endingMileage (index.php:407), not on navsData
			if (!storedEndingMileage && app.data.endingMileage) {
				const endingMileageInput = document.getElementById("ending-mileage");
				if (endingMileageInput) {
					endingMileageInput.value = app.data.endingMileage;
					consoleMsg("Ending mileage populated from NAVS:", app.data.endingMileage);
				}
			}

			// Previous route comes from the flow manager — no vendor/direction logic needed.
			const previousPage = app.flowManager.getPrevious(app.routes.signature);
			app.state.backButton.addEventListener("click", () => previous(previousPage.route));

			app.state.nextButton.addEventListener("click", async function (event) {
				await validatePage();

				// Check if current page (page10) validation passed
				const validPages = app.validator.getValidPages();
				if (!validPages.includes("page10")) {
					// Current page validation failed, stop here (error already shown)
					consoleMsg("Signature page validation failed - stopping");
					return;
				}

				consoleMsg("Valid page count: ", app.validator.getPageCount());
				const invalidPages = [];
				consoleMsg(validPages);
				// Reset the alert message to prevent duplicates on retry
				missingDataAlert.message = ` <p>You have not completed entering all required data. Please ensure you have
			answered all required questions on each page of the inspection form.  You are missing data for the following pages:
         </p>
         `;

				// Required pages come from the flow manager — the current vendor/direction
				// flow already knows exactly which pages must be complete for submission.
				for (const page of app.flowManager.getFlow()) {
					if (!validPages.includes(page.pageId)) {
						invalidPages.push(page.label);
						missingDataAlert.message += `- ${page.label} <br>`;
					}
				}

				// Check if any invalid pages have been pushed
				if (invalidPages.length > 0) {
					consoleMsg("Missing required page data");
					missingDataAlert.present();
					return;
				}

				consoleMsg(invalidPages);
				let data = {};
				if (signaturePad.isEmpty()) {
					await signatureAlert.present();
					return;
				} else {
					const signature = capturedSignature ?? signaturePad.toDataURL();
					// Retrieve form data keys from local storage
					Object.keys(localStorage).forEach((key) => {
						const value = localStorage.getItem(key);
						data[key] = value;
					});
					await store.set(`${app.state.storageKey}_signature`, signature);

					const activityId = new URLSearchParams(window.location.search).get("activity_id") || "dev";
					localStorage.setItem("submission-in-progress", activityId);

					const loading = document.createElement("ion-loading");
					loading.message = "Generating report and submitting...";
					document.body.appendChild(loading);
					await loading.present();

					try {
						await generateInspectionPDF(combinedVehicleDetails);
						await buildSubmissionData();

						// Log submission payload for server troubleshooting
						console.log("=== SUBMISSION PAYLOAD ===");
						console.log(
							JSON.stringify(
								{
									...app.submissionData,
									// Truncate large base64 strings for readability
									inspectionImages: app.submissionData.inspectionImages?.map((img, i) => `[Image ${i}: ${img?.length || 0} chars]`),
									pdfReport: app.submissionData.pdfReport ? `[PDF: ${app.submissionData.pdfReport.length} chars]` : null,
									damageIssues: app.submissionData.damageIssues?.map((issue) => ({
										...issue,
										photo: issue.photo ? `[Photo: ${issue.photo.length} chars]` : undefined,
										photos: issue.photos?.map((p, j) => `[Photo ${j}: ${p?.length || 0} chars]`),
									})),
								},
								null,
								2,
							),
						);
						console.log("=========================");

						const result = await navsSubmit();
						console.log(`RESULT:  ${result}`);

						// Show new submission messages
						if (result && result.success) {
							await displaySubmissionMessage(result.payload, true, result.status);
						} else {
							await displaySubmissionMessage(result?.payload, false, result?.status);
						}
					} catch (error) {
						/* submission-in-progress is deliberately left set: on the next live startup
						clearStorageOnStartup sees it and preserves the inspection instead of wiping it */
						console.error("Submission failed: ", error);
						showToast("Something went wrong while generating the report. Your inspection data has been saved — please try again, or contact support if this keeps happening.", "danger");
					} finally {
						await loading.dismiss();
						loading.remove();
					}
				}
			});

			const resizeObserver = new ResizeObserver(() => resizeCanvas(canvas, signaturePad));
			resizeObserver.observe(canvas);
			this._resizeObserver = resizeObserver;
		});
		this.addEventListener("ionViewWillLeave", () => {
			// Re-enable the refresher when leaving the Signature page
			const refresher = document.querySelector("ion-refresher");
			if (refresher) refresher.disabled = false;
			if (this._resizeObserver) {
				this._resizeObserver.disconnect();
				this._resizeObserver = null;
			}
		});
	}
}
